<script>
    import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
    import { slide } from 'svelte/transition';
    import { isHeaderVisible } from '$stores/uiStore';
    import { user } from '$stores/sessionStore';
    import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
    import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
    import { onMount, onDestroy } from 'svelte';
    import {
      effectiveAssignments,
      currentAssignmentHasSites,
      assignmentForProtocol,
      hasUnrestrictedAccess
    } from '$utils/userAssignments.js';
    import { applyAssignment } from '$lib/assignmentLanding';
    import { studies } from '$lib/api';
    import { navigate as goto } from 'svelte-routing';
    
    export let title = '';
    
    $: unrestricted = hasUnrestrictedAccess($user);
    $: assignments = effectiveAssignments($user);
    $: canSwitchStudy = assignments.length > 1;
    $: currentStudyIsSiteScoped = currentAssignmentHasSites($user, $selectedStudy?.protocolNumber);

    let assignedStudies = [];
    let isSwitcherOpen = false;
    let isLoadingStudies = false;
    let loadedKey = '';

    $: assignmentKey = assignments.map((row) => row.protocolNumber).join(',');
    $: if (canSwitchStudy && assignmentKey !== loadedKey) {
      loadedKey = assignmentKey;
      loadAssignedStudies(assignments);
    } else if (!canSwitchStudy) {
      assignedStudies = [];
      loadedKey = '';
    }

    async function loadAssignedStudies(rows) {
      if (!rows?.length) {
        assignedStudies = [];
        return;
      }
      isLoadingStudies = true;
      try {
        const results = await Promise.all(rows.map(async (row) => {
          try {
            const study = await studies.byProtocol(row.protocolNumber);
            return study || { protocolNumber: row.protocolNumber, studyName: row.protocolNumber };
          } catch (_) {
            return { protocolNumber: row.protocolNumber, studyName: row.protocolNumber };
          }
        }));
        assignedStudies = results.filter(Boolean);
      } finally {
        isLoadingStudies = false;
      }
    }

    function handleClickOutside(event) {
      if (isSwitcherOpen && !event.target.closest('.study-switcher-container')) {
        isSwitcherOpen = false;
      }
    }

    onMount(() => {
      document.addEventListener('click', handleClickOutside);
    });
    onDestroy(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    async function handleStudySwitch(study) {
      isSwitcherOpen = false;
      if (!study || study._id === $selectedStudy?._id) return;
      const assignment = assignmentForProtocol($user, study.protocolNumber);
      if (!assignment) return;
      try {
        const path = await applyAssignment(assignment);
        if (path) goto(path);
      } catch (err) {
        console.error('Error switching study:', err);
      }
    }
</script>
  
{#if $isHeaderVisible}
<div class="flex justify-between ph-2 pb-0" transition:slide|local>
    <h2 class="text-s mx-2 mb-1 text-sky-800 dark:text-sky-600">
        {#if unrestricted}
            <a href="/studies" class="hover:underline">Home</a>
        {:else}
            <span>Home</span>
        {/if}
        {#if $selectedStudy}
            | {#if canSwitchStudy}
                <span class="study-switcher-container relative inline-flex items-center">
                  <button
                    type="button"
                    class="inline-flex items-center hover:underline"
                    title="Switch to another assigned study"
                    on:click|stopPropagation={() => (isSwitcherOpen = !isSwitcherOpen)}
                    disabled={isLoadingStudies}
                  >
                    Study: {$selectedStudy.studyName} ({$selectedStudy.protocolNumber})
                    <FontAwesomeIcon icon={faChevronDown} class="ml-1 text-xs" />
                  </button>
                  {#if isSwitcherOpen && !isLoadingStudies}
                    <div class="absolute left-0 top-full mt-1 z-[9999] min-w-[16rem] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {#each assignedStudies as study}
                        <button
                          type="button"
                          class="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          on:click={() => handleStudySwitch(study)}
                        >
                          <div class="font-medium">{study.studyName || study.protocolNumber}</div>
                          <div class="text-xs text-gray-500 dark:text-gray-400">{study.protocolNumber}</div>
                        </button>
                      {/each}
                    </div>
                  {/if}
                </span>
            {:else if !currentStudyIsSiteScoped}
                <a href="/sites" class="hover:underline">Study: {$selectedStudy.studyName} ({$selectedStudy.protocolNumber})</a>
            {:else}
                <span>Study: {$selectedStudy.studyName} ({$selectedStudy.protocolNumber})</span>
            {/if}
        {/if}
        {#if $selectedSite}
            | <a href="/persons" class="hover:underline">Site: {$selectedSite.siteName} ({$selectedSite.siteId})</a>
        {/if}
        {#if $selectedParticipant}
            | <a href="/person" class="hover:underline">Subject: {$selectedParticipant.subjectId}</a>
        {/if}
        {#if title !== null}
            {title}
        {/if}
    </h2> 
</div>
{/if}
